/**
 * Local state, AI creative state, and bootstrap effects for Edit Sent form.
 */

import { useMemo, useState } from 'react';

import type { BulkUpdatePreparedDropsPayload } from '@/hooks';
import type {
  ClickIdStructureValue,
  DomainSchemeValue,
} from '@/features/smswiser-campaigns/utils/shortlinkOptionState';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { useEditSentDropBootstrap } from './useEditSentDropBootstrap';
import { useEditSentDropCreativeAiState } from './useEditSentDropCreativeAiState';

interface Args {
  drops: PreparedDrop[];
  initialApplyPayload?: BulkUpdatePreparedDropsPayload | null;
  onInitialApplyConsumed?: () => void;
}

export function useEditSentDropBaseState({ drops, initialApplyPayload, onInitialApplyConsumed }: Args) {
  const drop = drops[0] ?? null;
  const isBulk = drops.length > 1;
  const dropsKey = useMemo(() => drops.map((d) => d.id).join(','), [drops]);

  const [routeId, setRouteId] = useState<number | ''>(0);
  const [tldValue, setTldValue] = useState('');
  const [title, setTitle] = useState('');
  const [selectedSenderNames, setSelectedSenderNames] = useState<string[]>([]);
  const [selectedSmsTexts, setSelectedSmsTexts] = useState<string[]>([]);
  const [senderSearch, setSenderSearch] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [newSenderValue, setNewSenderValue] = useState('');
  const [newTextValue, setNewTextValue] = useState('');
  const [localSenders, setLocalSenders] = useState<string[]>([]);
  const [localTexts, setLocalTexts] = useState<string[]>([]);
  const [forceSend, setForceSend] = useState<boolean | 'unchanged'>(isBulk ? 'unchanged' : false);
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState<boolean | 'unchanged'>(isBulk ? 'unchanged' : false);
  // Seeded per drop by the bootstrap effect; bulk keeps each drop's own until picked.
  const [clickIdStructure, setClickIdStructure] = useState<ClickIdStructureValue>('unchanged');
  const [domainScheme, setDomainScheme] = useState<DomainSchemeValue>('unchanged');

  const creativeAi = useEditSentDropCreativeAiState(
    dropsKey,
    selectedSenderNames,
    setSelectedSenderNames,
    selectedSmsTexts,
    setSelectedSmsTexts,
    setLocalSenders,
    setLocalTexts,
  );

  useEditSentDropBootstrap({
    drop,
    isBulk,
    initialApplyPayload,
    onInitialApplyConsumed,
    setRouteId,
    setTldValue,
    setTitle,
    setSelectedSenderNames,
    setSelectedSmsTexts,
    setForceSend,
    setUseOnlyNewDomains,
    setClickIdStructure,
    setDomainScheme,
  });

  return {
    drop,
    isBulk,
    routeId,
    setRouteId,
    tldValue,
    setTldValue,
    title,
    setTitle,
    selectedSenderNames,
    setSelectedSenderNames,
    selectedSmsTexts,
    setSelectedSmsTexts,
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    localSenders,
    setLocalSenders,
    localTexts,
    setLocalTexts,
    forceSend,
    setForceSend,
    useOnlyNewDomains,
    setUseOnlyNewDomains,
    clickIdStructure,
    setClickIdStructure,
    domainScheme,
    setDomainScheme,
    creativeAi,
  };
}
